import { Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material'
import { AppointmentMinDTO } from '@services/dtos'
import { CalendarX2, Clock, FlaskConical, Stethoscope, X } from 'lucide-react'
import React from 'react'

type Props = {
  isOpen: boolean
  onClose: () => void
  noShows: AppointmentMinDTO[]
}

export const MissedAppointment: React.FC<Props> = ({
  isOpen,
  onClose,
  noShows,
}) => {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 5, maxHeight: 900 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}
      >
        <CalendarX2 size={20} />
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          Absences non justifiées
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: '20px 20px 24px', bgcolor: '#f8f9fc' }}>
        {noShows.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {noShows.map((appt, idx) => {
              const startDate = new Date(appt.start)
              const endDate = new Date(appt.end)
              const labels = appt.order.medicalOrders
                .map((mo) => mo.exam?.label)
                .filter(Boolean)
                .join(', ')
              const codes = appt.order.medicalOrders
                .map((mo) => mo.exam?.code)
                .filter(Boolean)
                .join(', ')
              const doctor = appt.order.attendingDoctor
                ? `Dr. ${appt.order.attendingDoctor.lastName}`
                : '—'

              return (
                <div
                  key={idx}
                  style={{
                    background: 'white',
                    borderRadius: 14,
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 14,
                    border: '1px solid #e8eaf0',
                  }}
                >
                  {/* timeline dot + line */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#ef4444',
                        flexShrink: 0,
                        marginTop: 3,
                      }}
                    />
                    {idx < noShows.length - 1 && (
                      <div
                        style={{
                          flex: 1,
                          width: 2,
                          background:
                            'linear-gradient(to bottom, #e0e0e0, transparent)',
                          borderRadius: 2,
                          minHeight: 20,
                        }}
                      />
                    )}
                  </div>

                  {/* content */}
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: '#1e1b4b',
                        }}
                      >
                        {startDate.toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#6366f1',
                          background: '#eef2ff',
                          borderRadius: 20,
                          padding: '2px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Clock size={10} />
                        {startDate.toLocaleTimeString('fr-FR', {
                          timeStyle: 'short',
                        })}{' '}
                        –{' '}
                        {endDate.toLocaleTimeString('fr-FR', {
                          timeStyle: 'short',
                        })}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: 16 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 6,
                          flex: 1,
                        }}
                      >
                        <FlaskConical
                          size={13}
                          color="#94a3b8"
                          style={{ marginTop: 2, flexShrink: 0 }}
                        />
                        <div>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#374151',
                            }}
                          >
                            {labels || '—'}
                          </p>
                          {codes && (
                            <p
                              style={{
                                margin: 0,
                                fontSize: 11,
                                color: '#94a3b8',
                              }}
                            >
                              {codes}
                            </p>
                          )}
                        </div>
                      </div>

                      <Divider orientation="vertical" flexItem />

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <Stethoscope
                          size={13}
                          color="#94a3b8"
                          style={{ flexShrink: 0 }}
                        />
                        <p
                          style={{
                            margin: 0,
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#374151',
                          }}
                        >
                          {doctor}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CalendarX2
              size={40}
              color="#d1d5db"
              style={{ margin: '0 auto 12px' }}
            />
            <Typography variant="body2" color="text.secondary">
              Aucun no-show enregistré pour ce patient.
            </Typography>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
